import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  format,
  getDate,
  isAfter,
  setDate,
  startOfMonth,
  subDays,
} from 'date-fns';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import type { LocalDate } from '@zg-rentals/ts-rent-payment-client';

interface PossibleEndDatesInterface {
  mustShortenTerm: boolean;
  finalDueDate1: string | Date;
  lastDayOfTerm1: string | Date;
  finalDueDate2: string | Date;
  lastDayOfTerm2: string | Date;
}

export const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Returns a formatted string to specify the distance between two dates.
 * eg. '2018-01-10', '2018-01-15' => 'in 5 days'
 * eg. '2018-01-10', '2018-01-08' => '2 days ago'
 * eg. '2018-01-10', '2018-01-10' => 'today'
 * @param {date} earlierDate
 * @param {date} laterDate
 * @returns {string}
 */
export const printDaysBetweenDates = (earlierDate: string | Date, laterDate: string | Date): string => {
  const difference = differenceInCalendarDays(laterDate, earlierDate);
  if (difference === 0) {
    return 'today';
  }

  if (difference === 1) {
    return 'tomorrow';
  }

  if (difference === -1) {
    return 'yesterday';
  }

  if (difference > 0) {
    return `in ${difference} days`;
  }

  return `${Math.abs(difference)} days ago`;
};

/**
 * Converts various input types (Date, string, number) to a Date object.
 * For ISO date strings (YYYY-MM-DD), treats them as local calendar dates rather than UTC timestamps,
 * ensuring '2025-11-01' displays as "November 1st" regardless of the user's timezone.
 * Returns undefined if the input cannot be parsed into a valid date.
 * @param {string | Date | number} input - The value to convert to a Date
 * @returns {Date | undefined}
 */
export function coerceToDate(input: string | Date | number): Date | undefined {
  if (input instanceof Date) {
    return isNaN(input.getTime()) ? undefined : input;
  }
  if (typeof input === 'number') {
    const d = new Date(input);
    return isNaN(d.getTime()) ? undefined : d;
  }
  const str = String(input).trim();
  const lower = str.toLowerCase();
  if (!str || lower === 'invalid date' || lower === 'n/a' || str === '-') {
    return undefined;
  }
  // If input looks like ISO (YYYY-MM-DD), parse as local date to avoid timezone issues
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const y = parseInt(year, 10);
    const m = parseInt(month, 10) - 1;
    const d = parseInt(day, 10);
    // Create date in local timezone by using Date constructor with explicit components
    const date = new Date(y, m, d);
    // Strict validation: check if JS rolled over an invalid date (e.g., 2025-02-31 → March 3)
    if (isNaN(date.getTime()) || date.getFullYear() !== y || date.getMonth() !== m || date.getDate() !== d) {
      return undefined;
    }
    return date;
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? undefined : d;
}

/**
 * Returns a string to specify the month day or today/tomorrow/yesterday
 * eg. '2018-01-10', '2018-01-15' => 'January 15th'
 * eg. '2018-01-10', '2018-01-11' => 'tomorrow'
 * eg. '2018-01-10', '2018-01-10' => 'today'
 * @param {string | Date} baseDate - The earlier date to compare from
 * @param {string | Date} dateInQuestion - The later date to format
 * @returns {string}
 */
export const printRelativeDateString = (baseDate: string | Date, dateInQuestion: string | Date): string => {
  const base = coerceToDate(baseDate);
  const target = coerceToDate(dateInQuestion);
  if (!base || !target) {
    return 'N/A';
  }
  const difference = differenceInCalendarDays(target, base);
  if (Math.abs(difference) <= 1) {
    return printDaysBetweenDates(base, target);
  }

  return format(target, 'MMMM do');
};

function setDueDay(date: string | Date, day: number): Date {
  const latestPossibleDay = getDate(endOfMonth(coerceToDate(date)!));
  const dueDay = day > latestPossibleDay ? latestPossibleDay : day;
  return setDate(date, dueDay);
}

function getLastDayOfTerm(dueDateMonth: string | Date, dueDay: number): Date {
  const nextMonth = addMonths(dueDateMonth, 1);
  const nextDueDay = setDueDay(nextMonth, dueDay);
  return subDays(nextDueDay, 1);
}

// currentTermEndDate === currentTerm.endDateForPayments e.g. last due date
export const getPossibleEndDates = (
  currentTermEndDate: string | Date,
  today: Date = new Date(),
): PossibleEndDatesInterface => {
  today = coerceToDate(today) ?? new Date();

  const lastDueDate = coerceToDate(currentTermEndDate)!;
  const dueDay = getDate(lastDueDate);
  const thisMonth = startOfMonth(today);
  const nextMonth = addMonths(thisMonth, 1);
  const mustShortenTerm = isAfter(lastDueDate, addMonths(today, 2));
  const dueDateMonth1 = mustShortenTerm ? nextMonth : startOfMonth(lastDueDate);
  const dueDateMonth2 = addMonths(dueDateMonth1, 1);
  const finalDueDate1 = setDueDay(dueDateMonth1, dueDay);
  const lastDayOfTerm1 = getLastDayOfTerm(dueDateMonth1, dueDay);
  const finalDueDate2 = setDueDay(dueDateMonth2, dueDay);
  const lastDayOfTerm2 = getLastDayOfTerm(dueDateMonth2, dueDay);

  return {
    mustShortenTerm,
    finalDueDate1,
    lastDayOfTerm1,
    finalDueDate2,
    lastDayOfTerm2,
  };
};

export const getFirstPossibleDueDate = ({ paymentDayOfTheMonth, lastDayOfTerm }): Date => {
  const lastDayOfTermDate = coerceToDate(lastDayOfTerm)!;
  let dueDate = setDate(lastDayOfTermDate, paymentDayOfTheMonth);

  if (!isAfter(dueDate, lastDayOfTermDate)) {
    dueDate = addMonths(dueDate, 1);
  }
  return dueDate;
};

/**
 * Returns a date to restrict the Rachel to not select a date more than 20 days after the due date.
 * eg. '2018-01-10' => '2018-01-30'
 * @param {string | date} dueDate
 * @returns {date}
 */
export const getLastSelectablePaymentDate = (dueDate: string | Date): Date => {
  const baseDate = coerceToDate(dueDate) ?? new Date(dueDate as any);
  let maxDate = baseDate;
  maxDate = addDays(maxDate, 20);
  return maxDate;
};

/**
 * Returns N/A if date provided is invalid (N/A)
 * otherwise format the date in the given format
 * @param {string | Date} dateStr
 * @param {string} dateFormat
 * @returns {string}
 */
export const formatDate = (dateStr: string | Date | number, dateFormat: string, pontoonMetric?: string): string => {
  const dateObj = coerceToDate(dateStr);
  if (!dateObj) {
    if (pontoonMetric) {
      const pontoonMetricName = 'Exception Invalid Date '.concat(pontoonMetric);
      getGlobalMonitor()?.count({ name: pontoonMetricName });
    }
    return 'N/A';
  }
  try {
    return format(dateObj, dateFormat);
  } catch (_) {
    if (pontoonMetric) {
      const pontoonMetricName = 'Exception Invalid Date '.concat(pontoonMetric);
      getGlobalMonitor()?.count({ name: pontoonMetricName });
    }
    return 'N/A';
  }
};

/**
 * Returns String in UTC if date provided is Timestamp
 * @param {Number} dateToConvert
 * @returns {string}
 */
export const formatTimestampToYMDString = (dateToConvert: number | undefined): string => {
  if (dateToConvert === 0 || !dateToConvert) {
    return '';
  }
  const d = new Date(dateToConvert);
  if (isNaN(d.getTime())) {
    return 'N/A';
  }
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Returns UTC milliseconds timestamp if YMD string is input
 */
export const formatYMDStringToTimestamp = (ymd: string): number => Date.parse(ymd);

export const toUTCTimestamp = (d?: LocalDate | number): number | undefined => {
  if (!d) return undefined;
  if (typeof d === 'number') return d;
  const mm = String(d.month).padStart(2, '0');
  const dd = String(d.day).padStart(2, '0');
  return Date.parse(`${d.year}-${mm}-${dd}`);
};

/**
 * Converts a LocalDate object to "YYYY-MM-DD" string.
 * LocalDate represents a calendar date without timezone, so no conversion needed.
 */
export const localDateToISOString = (d: LocalDate): string => {
  const mm = String(d.month).padStart(2, '0');
  const dd = String(d.day).padStart(2, '0');
  return `${d.year}-${mm}-${dd}`;
};
